import { createRoot } from '@wordpress/element';
import { useState, useEffect, useRef, useCallback } from '@wordpress/element';

// ============================================================================
// Validation
// ============================================================================

const validators = {
	company:  (v) => v.trim().length >= 2 ? '' : 'Company name required',
	contact:  (v) => v.trim().length >= 2 ? '' : 'Contact name required',
	email:    (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? '' : 'Valid email required',
	phone:    () => '', // optional
	country:  (v) => ['US', 'CA'].includes(v) ? '' : 'Country required',
	address:  (v) => v.trim().length >= 3 ? '' : 'Street address required',
	city:     (v) => v.trim().length >= 2 ? '' : 'City required',
	state:    (v) => v.trim().length >= 2 ? '' : 'State / province required',
	zip:      (v) => v.trim().length >= 3 ? '' : 'Postal / ZIP code required',
	notes:    () => '',
};

// ============================================================================
// Google Places loader — same pattern as checkout-form/view.js
// ============================================================================

function useGooglePlaces(apiKey) {
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		if (!apiKey) { setLoaded(false); return; }
		if (window.google?.maps?.places) { setLoaded(true); return; }
		if (window.__googlePlacesLoading) {
			window.__googlePlacesLoadedCallbacks = window.__googlePlacesLoadedCallbacks || [];
			window.__googlePlacesLoadedCallbacks.push(() => setLoaded(true));
			return;
		}
		window.__googlePlacesLoading = true;
		const script = document.createElement('script');
		script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
		script.async = true;
		script.onload = () => {
			setLoaded(true);
			if (window.__googlePlacesLoadedCallbacks) {
				window.__googlePlacesLoadedCallbacks.forEach(cb => cb());
				delete window.__googlePlacesLoadedCallbacks;
			}
		};
		script.onerror = () => setLoaded(false);
		document.head.appendChild(script);
	}, [apiKey]);

	return loaded;
}

// ============================================================================
// AddressInput — text input with Google Places autocomplete (US/CA only)
// ============================================================================

function AddressInput({ id, value, onChange, country, googleApiKey, onPlaceSelect, error, placeholder, label, required }) {
	const inputRef = useRef(null);
	const autocompleteRef = useRef(null);
	const googleLoaded = useGooglePlaces(googleApiKey);

	useEffect(() => {
		if (!['US', 'CA'].includes(country) || !googleLoaded || !inputRef.current) {
			if (autocompleteRef.current && window.google?.maps?.event) {
				window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
				autocompleteRef.current = null;
			}
			return;
		}
		if (!window.google?.maps?.places?.Autocomplete) return;
		if (autocompleteRef.current) {
			window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
			autocompleteRef.current = null;
		}
		autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
			componentRestrictions: { country: country.toLowerCase() },
			fields: ['formatted_address', 'address_components'],
			types: ['address'],
		});
		autocompleteRef.current.addListener('place_changed', () => {
			const place = autocompleteRef.current.getPlace();
			if (!place.address_components) return;
			const ad = {};
			place.address_components.forEach(c => {
				const t = c.types;
				if (t.includes('street_number')) ad.number = c.long_name;
				if (t.includes('route')) ad.street = c.long_name;
				if (t.includes('locality')) ad.city = c.long_name;
				if (t.includes('administrative_area_level_1')) ad.state = c.short_name;
				if (t.includes('postal_code')) ad.zip = c.long_name;
			});
			if (onPlaceSelect) {
				onPlaceSelect({
					address: `${ad.number || ''} ${ad.street || ''}`.trim(),
					city:    ad.city  || '',
					state:   ad.state || '',
					zip:     ad.zip   || '',
				});
			}
		});
	}, [googleLoaded, country, onPlaceSelect]);

	return (
		<div className="form-group">
			<input
				ref={inputRef}
				id={id}
				type="text"
				className={`form-input${error ? ' is-invalid' : ''}`}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder || label}
				autoComplete="street-address"
			/>
			<label className="form-label" htmlFor={id}>{label}{required && ' *'}</label>
			{error && <div style={{ fontSize: '13px', color: '#D42B41', marginTop: '4px' }}>{error}</div>}
		</div>
	);
}

// ============================================================================
// Plain text input (no autocomplete)
// ============================================================================

function TextInput({ id, type = 'text', value, onChange, error, placeholder, label, required, autoComplete }) {
	return (
		<div className="form-group">
			<input
				id={id}
				type={type}
				className={`form-input${error ? ' is-invalid' : ''}`}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder || label}
				autoComplete={autoComplete}
			/>
			<label className="form-label" htmlFor={id}>{label}{required && ' *'}</label>
			{error && <div style={{ fontSize: '13px', color: '#D42B41', marginTop: '4px' }}>{error}</div>}
		</div>
	);
}

// ============================================================================
// Main app
// ============================================================================

function QuoteApp({ data: initialData }) {
	const uid = useRef('qf_' + Math.random().toString(36).slice(2, 10)).current;
	// Anti-bot timing: captured at first render. Server requires >= 2s elapsed.
	const mountTs = useRef(Date.now()).current;

	// Calculator state
	const [teamSize, setTeamSize] = useState(initialData.minPeople);
	const [delivery, setDelivery] = useState('bulk'); // 'bulk' | 'direct'

	// Form state
	const [form, setForm] = useState({
		company: '', contact: '', email: '', phone: '',
		country: 'US', address: '', city: '', state: '', zip: '',
		notes: '',
		hp: '', // honeypot
	});
	const [errors, setErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [feedback, setFeedback] = useState({ type: '', message: '' });

	const update = (key) => (val) => {
		setForm(prev => ({ ...prev, [key]: val }));
		if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
	};

	const onPlaceSelect = useCallback(({ address, city, state, zip }) => {
		setForm(prev => ({
			...prev,
			address: address || prev.address,
			city:    city    || prev.city,
			state:   state   || prev.state,
			zip:     zip     || prev.zip,
		}));
	}, []);

	const validate = () => {
		const e = {};
		Object.keys(validators).forEach(k => {
			const msg = validators[k](form[k] || '');
			if (msg) e[k] = msg;
		});
		setErrors(e);
		return Object.keys(e).length === 0;
	};

	const submit = async (ev) => {
		ev.preventDefault();
		if (!validate()) return;
		setSubmitting(true);
		setFeedback({ type: '', message: '' });

		try {
			const res = await fetch(`${initialData.restUrl}quote`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': initialData.nonce },
				body: JSON.stringify({
					company: form.company,
					contact: form.contact,
					email:   form.email,
					phone:   form.phone,
					country: form.country,
					address: form.address,
					city:    form.city,
					state:   form.state,
					zip:     form.zip,
					notes:   form.notes,
					participants: teamSize,
					delivery,
					hp: form.hp,
					_ts: mountTs,
				}),
			});
			const json = await res.json();
			if (res.ok && json.success) {
				setFeedback({ type: 'success', message: json.message || 'Thank you! Your request has been submitted.' });
				setForm({
					company: '', contact: '', email: '', phone: '',
					country: 'US', address: '', city: '', state: '', zip: '',
					notes: '', hp: '',
				});
			} else {
				setFeedback({ type: 'error', message: json.message || 'Something went wrong. Please try again.' });
			}
		} catch {
			setFeedback({ type: 'error', message: 'Network error. Please try again.' });
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="grid">
			{/* Left: calculator */}
			<div className="col-7 col-md-12">
				<div className="atw-calculator border rounded h-100">
					<div className="atw-calc-group">
						<div className="d-flex justify-between align-center">
							<span className="atw-calc-label">Team size</span>
							<span className="atw-calc-value">{teamSize}</span>
						</div>
						<input
							type="range"
							className="atw-slider"
							min={initialData.minPeople}
							max={initialData.maxPeople}
							step={initialData.step}
							value={teamSize}
							onChange={(e) => setTeamSize(parseInt(e.target.value, 10))}
						/>
						<div className="d-flex justify-between">
							<span className="atw-calc-range">{initialData.minPeople}</span>
							<span className="atw-calc-range">{initialData.maxPeople}+</span>
						</div>
					</div>

					<div className="atw-calc-group">
						<span className="atw-calc-label">Delivery method</span>
						<div className="atw-delivery-toggle mt-1">
							<label className={`atw-delivery-option${delivery === 'bulk' ? ' atw-delivery-active' : ''}`}>
								<input
									type="radio"
									name={`delivery-${uid}`}
									value="bulk"
									checked={delivery === 'bulk'}
									onChange={() => setDelivery('bulk')}
								/>
								<span className="atw-delivery-btn">
									<strong>Bulk shipment</strong>
									<small>Free, shipped to one address</small>
								</span>
							</label>
							<label className={`atw-delivery-option${delivery === 'direct' ? ' atw-delivery-active' : ''}`}>
								<input
									type="radio"
									name={`delivery-${uid}`}
									value="direct"
									checked={delivery === 'direct'}
									onChange={() => setDelivery('direct')}
								/>
								<span className="atw-delivery-btn">
									<strong>Direct to employee</strong>
									<small>Quoted separately, individual addresses</small>
								</span>
							</label>
						</div>
					</div>

					<div className="atw-calc-included mt-3">
						<span className="atw-calc-label">What's included</span>
						<ul className="atw-included-list mt-1">
							<li>1 iAmEvolving™ Journal per person</li>
							<li>Quick Start Guide (printed, in each journal)</li>
							<li>Welcome Card for each participant</li>
							<li>Leader's Guide PDF (digital, emailed to you)</li>
						</ul>
					</div>

					{delivery === 'direct' && (
						<div className="atw-calc-group mt-3" style={{ fontSize: '13px', color: '#666' }}>
							We'll email you a simple address-list template after you submit so you can provide the individual shipping addresses.
						</div>
					)}
				</div>
			</div>

			{/* Right: form */}
			<div className="col-5 col-md-12">
				<div className="atw-quote-form">
					<h3 className="atw-quote-title">Request a Quote</h3>
					<p className="atw-quote-sub">We'll get back to you within one business day.</p>

					<form className="contact-form" onSubmit={submit} noValidate>
						<style>{`.iae-hp-field{position:absolute!important;left:-9999px!important;top:-9999px!important;height:0!important;width:0!important;overflow:hidden!important;opacity:0!important;pointer-events:none!important;}`}</style>
						<input
							type="text"
							className="iae-hp-field"
							tabIndex={-1}
							autoComplete="off"
							aria-hidden="true"
							value={form.hp}
							onChange={(e) => setForm(prev => ({ ...prev, hp: e.target.value }))}
						/>

						<div className="grid gap-sm">
							{/* Team size + delivery mirror the calculator state so the user
							    can see what's being submitted. Read-only — change via the
							    slider / toggle on the left. */}
							<div className="col-6 col-sm-12">
								<div className="form-group">
									<input
										id={`${uid}-participants`}
										type="text"
										className="form-input prefilled"
										value={String(teamSize)}
										readOnly
										tabIndex={-1}
									/>
									<label className="form-label" htmlFor={`${uid}-participants`}>Participants</label>
								</div>
							</div>
							<div className="col-6 col-sm-12">
								<div className="form-group">
									<input
										id={`${uid}-delivery-label`}
										type="text"
										className="form-input prefilled"
										value={delivery === 'direct' ? 'Direct to employee' : 'Bulk (one address)'}
										readOnly
										tabIndex={-1}
									/>
									<label className="form-label" htmlFor={`${uid}-delivery-label`}>Delivery</label>
								</div>
							</div>

							<div className="col-6 col-sm-12">
								<TextInput id={`${uid}-company`} value={form.company} onChange={update('company')} error={errors.company} label="Company Name" required autoComplete="organization" />
							</div>
							<div className="col-6 col-sm-12">
								<TextInput id={`${uid}-contact`} value={form.contact} onChange={update('contact')} error={errors.contact} label="Contact Name" required autoComplete="name" />
							</div>
							<div className="col-6 col-sm-12">
								<TextInput id={`${uid}-email`} type="email" value={form.email} onChange={update('email')} error={errors.email} label="Email" required autoComplete="email" />
							</div>
							<div className="col-6 col-sm-12">
								<TextInput id={`${uid}-phone`} type="tel" value={form.phone} onChange={update('phone')} error={errors.phone} label="Phone" autoComplete="tel" />
							</div>

							{/* Country */}
							<div className="col-6 col-sm-12">
								<div className="form-group">
									<select
										id={`${uid}-country`}
										className={`form-input${errors.country ? ' is-invalid' : ''}`}
										value={form.country}
										onChange={(e) => update('country')(e.target.value)}
										autoComplete="country"
									>
										<option value="US">United States</option>
										<option value="CA">Canada</option>
									</select>
									<label className="form-label" htmlFor={`${uid}-country`}>Country *</label>
									{errors.country && <div style={{ fontSize: '13px', color: '#D42B41', marginTop: '4px' }}>{errors.country}</div>}
								</div>
							</div>

							{/* Address (autocomplete) */}
							<div className="col-6 col-sm-12">
								<AddressInput
									id={`${uid}-address`}
									value={form.address}
									onChange={update('address')}
									error={errors.address}
									label="Billing Address"
									required
									country={form.country}
									googleApiKey={initialData.googleApiKey}
									onPlaceSelect={onPlaceSelect}
								/>
							</div>

							<div className="col-4 col-sm-12">
								<TextInput id={`${uid}-city`} value={form.city} onChange={update('city')} error={errors.city} label="City" required autoComplete="address-level2" />
							</div>
							<div className="col-4 col-sm-12">
								<TextInput
									id={`${uid}-state`}
									value={form.state}
									onChange={update('state')}
									error={errors.state}
									label={form.country === 'CA' ? 'Province' : 'State'}
									required
									autoComplete="address-level1"
								/>
							</div>
							<div className="col-4 col-sm-12">
								<TextInput
									id={`${uid}-zip`}
									value={form.zip}
									onChange={update('zip')}
									error={errors.zip}
									label={form.country === 'CA' ? 'Postal Code' : 'ZIP Code'}
									required
									autoComplete="postal-code"
								/>
							</div>

							<div className="col-12">
								<div className="form-group">
									<textarea
										id={`${uid}-notes`}
										className="form-input form-textarea"
										rows="4"
										placeholder="Notes / PO #"
										value={form.notes}
										onChange={(e) => update('notes')(e.target.value)}
									/>
									<label className="form-label" htmlFor={`${uid}-notes`}>Notes / PO #</label>
								</div>
							</div>
						</div>

						<button type="submit" className="btn-primary w-100 mt-3" style={{ justifyContent: 'center' }} disabled={submitting}>
							{submitting ? 'Sending…' : 'Request a Quote →'}
						</button>

						{feedback.message && (
							<div className={`iae-form-feedback mt-3`} aria-live="polite">
								<div className={feedback.type === 'success' ? 'form-feedback-success' : 'form-feedback-error'}>
									{feedback.message}
								</div>
							</div>
						)}
					</form>
				</div>
			</div>
		</div>
	);
}

// ============================================================================
// Mount
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.wp-block-wpl-quote-form').forEach(container => {
		if (container.dataset.qfMounted === '1') return;
		container.dataset.qfMounted = '1';

		const d = container.dataset;
		const data = {
			minPeople:    parseInt(d.minPeople || '20', 10),
			maxPeople:    parseInt(d.maxPeople || '100', 10),
			step:         parseInt(d.step || '5', 10),
			googleApiKey: d.googleApiKey || '',
			restUrl:      d.restUrl,
			nonce:        d.nonce,
		};
		container.innerHTML = '';
		createRoot(container).render(<QuoteApp data={data} />);
	});
});
